import { derivePuzzleFromId } from "./derive-puzzle";
import type { Puzzle } from "./types";

/**
 * Off-calendar puzzles: instead of the UTC date, the player gets a blend
 * derived from an arbitrary seed token. The seed lives in the URL
 * (`/blend/:seed`), so a random blend is shareable and survives a
 * reload, and its attempts get their own `localStorage` slot —
 * namespaced under `random-` so they never collide with a `YYYY-MM-DD`
 * daily id.
 */

const RANDOM_PUZZLE_ID_PREFIX = "random-";

/**
 * A fresh, URL-safe seed for a new random blend. 8 bytes of CSPRNG
 * output as lowercase base36 (`0-9a-z`, ≤13 chars) — no escaping needed
 * in a path segment, and short enough to read out loud.
 *
 * Browser-only (uses `crypto.getRandomValues`); call it from an event
 * handler, never at module load or in a render body.
 */
export function newRandomBlendSeed(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let n = 0n;
  for (const b of bytes) n = (n << 8n) | BigInt(b);
  return n.toString(36);
}

/// The opaque storage id for a random blend. Distinct namespace from the
/// daily `YYYY-MM-DD` ids so attempt/draft state never bleeds across.
export function randomBlendPuzzleId(seed: string): string {
  return RANDOM_PUZZLE_ID_PREFIX + seed;
}

/// Resolves the random blend for a seed. Deterministic: the same seed
/// always yields the same blend, which is what makes the URL replayable.
export function getRandomBlendPuzzle(seed: string): Promise<Puzzle> {
  return derivePuzzleFromId(randomBlendPuzzleId(seed));
}
